/**
 * Song mode (owner, 2026-10-29): with a song chosen in the menu, each collected light plays the
 * song's next note on the pling sound instead of its colour's pitch, so a good round plays the
 * tune; it starts over every round and repeats when it ends. Only public-domain melodies are
 * written here. A song is just note names: add one by listing its notes, separated by spaces
 * (scientific pitch, "#" or "b", e.g. "C#5", "Bb4"; "|" between phrases is only for reading),
 * ideally between A4 and A6 (the pling sounds best there).
 */
export const SONGS = {
	// Edvard Grieg, I bergakungens sal (1875), the theme in A minor.
	bergakungen: {
		name: "Bergakungen",
		notes:
			"A4 B4 C5 D5 E5 C5 E5 | D#5 B4 D#5 | D5 Bb4 D5 | A4 B4 C5 D5 E5 C5 E5 A5 | G5 E5 C5 E5 G5",
	},
	// J. S. Bach, Toccata and Fugue in D minor, BWV 565 (c. 1704), the opening.
	toccata: {
		name: "Toccata",
		notes:
			"A5 G5 A5 | G5 F5 E5 D5 C#5 D5 | A4 G4 A4 | E4 F4 C#4 D4 | A4 G4 A4 | G4 F4 E4 D4 C#4 D4",
	},
} as const satisfies Record<string, { name: string; notes: string }>;

export type SongId = keyof typeof SONGS;
export const SONG_IDS = Object.keys(SONGS) as SongId[];

const STEPS: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
/** The pling sound is an A5 (880 Hz, MIDI 81). */
const PLING_MIDI = 81;

/** A song's notes in order (the "|" phrase marks dropped). */
export function songNotes(id: SongId): string[] {
	return SONGS[id].notes.split(/\s+/).filter((n) => n && n !== "|");
}

/** A note name ("C#5", "Bb4", "A5") as cents from the pling's own pitch; NaN if not a note. */
export function noteCents(note: string): number {
	const match = /^([A-G])([#b]?)(-?\d)$/.exec(note);
	if (!match) return Number.NaN;
	const [, letter = "", accidental, octave = ""] = match;
	const midi =
		12 * (Number(octave) + 1) +
		(STEPS[letter] ?? 0) +
		(accidental === "#" ? 1 : accidental === "b" ? -1 : 0);
	return (midi - PLING_MIDI) * 100;
}

/** Walks through a song one note per collect, repeating from the start when it ends. */
export class SongCursor {
	private index = 0;
	private readonly cents: number[];

	constructor(readonly id: SongId) {
		this.cents = songNotes(id).map(noteCents);
	}

	/** The next note's detune in cents. */
	next(): number {
		const cents = this.cents[this.index % this.cents.length] ?? 0;
		this.index++;
		return cents;
	}

	reset(): void {
		this.index = 0;
	}
}
